import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { MessageSquare, Search, Trash2 } from "lucide-react";

import Spinner from "../../components/spinner";
import Pagination from "../../components/pagination";
import DateRangeInput from "../../components/dateRangeInput";
import { useLanguage } from "../../context/languageContext";
import { Can, usePermissions } from "../../context/permissionContext";
import { commentsApi, formatWhen } from "./commentsApi";
import CommentDetail from "./CommentDetail";
import { useConfirm } from "../../components/confirmDialog";

/**
 * Comments — every comment readers have left on a post, newest first.
 *
 * Moderation only: there is nothing to create or edit here, because a comment
 * is somebody else's words — `comments` can only be granted view and delete.
 * The server records the text and its author in the activity log, so a removal
 * can be explained afterwards.
 *
 * The search and the date range live in the URL, so a link to "everything said
 * last Tuesday containing X" can be shared and survives a reload.
 */

const SEARCH_DELAY = 300;
const PAGE_SIZE = 25;

const Comments = () => {
  const { adminInfo } = useLanguage();
  const { can } = usePermissions();
  const canDelete = can("comments", "delete");
  const confirm = useConfirm();
  const api = useMemo(() => commentsApi(adminInfo?.token), [adminInfo?.token]);

  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";
  const dates = params.get("on") || "";
  const page = Math.max(parseInt(params.get("page"), 10) || 1, 1);

  const [searchInput, setSearchInput] = useState(query);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  // Ticked rows, keyed by comment id and holding the post id with it — a
  // comment lives inside its post, so the id alone cannot address it. Kept as
  // a Map so a selection can span pages: ticking five here, paging on and
  // ticking three more deletes eight.
  const [selected, setSelected] = useState(() => new Map());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // The row opened for reading. The table truncates nothing, but a long
  // comment is still a wall in a cell — and the post it answers is the half of
  // it the table cannot show.
  const [viewing, setViewing] = useState(null);

  const setUrl = useCallback(
    (changes) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  // Typing shouldn't fire a request per keystroke; a page change should be
  // immediate, so only the text is debounced.
  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput !== query) setUrl({ q: searchInput, page: "" });
    }, SEARCH_DELAY);
    return () => clearTimeout(id);
  }, [searchInput, query, setUrl]);

  const load = useCallback(async () => {
    if (!adminInfo?.token) return;
    setLoading(true);
    try {
      const [from = "", to = ""] = String(dates).split(",");
      const data = await api.list({
        page,
        limit: PAGE_SIZE,
        search: query || undefined,
        from: from || undefined,
        to: to || undefined,
      });
      setRows(data?.items || []);
      setTotal(data?.total || 0);
      setPages(data?.pages || 1);
    } catch (error) {
      toast.error(error.message);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [adminInfo?.token, api, dates, page, query]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRow = (row) => {
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(row._id)) next.delete(row._id);
      else next.set(row._id, { postId: row.postId, commentId: row._id, text: row.text });
      return next;
    });
  };

  const pageSelected = rows.length > 0 && rows.every((row) => selected.has(row._id));
  const someSelected = rows.some((row) => selected.has(row._id));

  const togglePage = () => {
    setSelected((current) => {
      const next = new Map(current);
      // All of this page ticked → the box clears this page, not the whole
      // selection: rows ticked on another page are not on screen to be seen
      // disappearing.
      rows.forEach((row) => {
        if (pageSelected) next.delete(row._id);
        else next.set(row._id, { postId: row.postId, commentId: row._id, text: row.text });
      });
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const picked = [...selected.values()];
    if (picked.length === 0) return;

    const ok = await confirm({
      title: `Delete ${picked.length} comment${picked.length === 1 ? "" : "s"}?`,
      body: "They are removed from the posts they were left on. This can't be undone.",
      quote: picked
        .slice(0, 5)
        .map((c) => `“${c.text}”`)
        .join("\n")
        .concat(picked.length > 5 ? `\n…and ${picked.length - 5} more` : ""),
      confirmLabel: `Delete ${picked.length}`,
    });
    if (!ok) return;

    setBulkDeleting(true);
    try {
      const result = await api.removeMany(
        picked.map(({ postId, commentId }) => ({ postId, commentId }))
      );
      const failed = result?.failed?.length || 0;
      const gone = result?.deleted?.length ?? picked.length - failed;
      // Part of a batch failing is reported as what it is — the rest went.
      if (failed > 0) {
        toast.warn(`${gone} deleted, ${failed} could not be`);
      } else {
        toast.success(`${gone} comment${gone === 1 ? "" : "s"} deleted`);
      }
      setSelected(new Map());
      await load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDelete = async (row) => {
    // Asked before doing: a comment cannot be put back, and the person who
    // wrote it is not the person deleting it.
    const who = row.authorName || "a deleted account";
    const ok = await confirm({
      title: "Delete this comment?",
      body: `Written by ${who}. This can't be undone.`,
      quote: row.text,
      confirmLabel: "Delete comment",
    });
    if (!ok) return;

    setDeletingId(row._id);
    try {
      await api.remove(row.postId, row._id);
      toast.success("Comment deleted");
      setSelected((current) => {
        if (!current.has(row._id)) return current;
        const next = new Map(current);
        next.delete(row._id);
        return next;
      });
      // Reload rather than splice: the page behind it has shifted up by one.
      await load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-3">
      <div className="d-flex align-items-center justify-content-between mb-1">
        <div>
          <h4 className="mb-1">Comments {total > 0 ? `(${total})` : ""}</h4>
          <p className="text-muted small mb-0">
            What readers have said on posts. Nothing here can be edited — only removed.
          </p>
        </div>
      </div>

      <div className="d-flex flex-column flex-lg-row gap-2 my-3">
        <div className="position-relative flex-grow-1">
          <Search
            size={16}
            className="position-absolute text-muted"
            style={{ left: 12, top: "50%", transform: "translateY(-50%)" }}
          />
          <input
            className="form-control form-control-sm ps-5"
            style={{ height: 40 }}
            placeholder="Search comment text..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <DateRangeInput
          className="form-control form-control-sm"
          ariaLabel="Commented"
          value={dates}
          onChange={(value) => setUrl({ on: value, page: "" })}
        />
      </div>

      {selected.size > 0 && (
        <div className="d-flex align-items-center gap-3 mb-3 p-2 px-3 box_wrapper">
          <span className="small">
            {selected.size} comment{selected.size === 1 ? "" : "s"} selected
          </span>
          <button
            type="button"
            className="btn btn-sm btn-link text-muted p-0"
            onClick={() => setSelected(new Map())}
          >
            Clear
          </button>
          <div className="ms-auto">
            <Can feature="comments" action="delete">
              <button
                type="button"
                className="btn btn-sm btn-danger d-inline-flex align-items-center gap-2"
                disabled={bulkDeleting}
                onClick={handleBulkDelete}
              >
                <Trash2 size={14} />
                {bulkDeleting ? "Deleting…" : "Delete selected"}
              </button>
            </Can>
          </div>
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className="text-center text-muted py-5">
          <MessageSquare size={28} className="mb-2" />
          <p className="mb-0">
            {query || dates ? "No comments match that." : "No comments yet."}
          </p>
        </div>
      ) : (
        <>
          <div className="table_scrollbar table-responsive">
            <table className="table">
              <thead>
                <tr>
                  {canDelete && (
                    <th>
                      <input
                        className="box form-check-input"
                        type="checkbox"
                        checked={pageSelected}
                        // Part of the page ticked is neither on nor off, and
                        // a plain empty box would read as "nothing selected".
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected && !pageSelected;
                        }}
                        onChange={togglePage}
                        aria-label="Select every comment on this page"
                      />
                    </th>
                  )}
                  <th className="index">S.no</th>
                  <th className="max_width">Comment</th>
                  <th>By</th>
                  <th>On post</th>
                  <th>Commented</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={row._id}
                    onClick={() => setViewing(row)}
                    style={{ cursor: "pointer" }}
                  >
                    {canDelete && (
                      <td className="checkBox" onClick={(e) => e.stopPropagation()}>
                        <input
                          className="box form-check-input"
                          type="checkbox"
                          checked={selected.has(row._id)}
                          onChange={() => toggleRow(row)}
                          aria-label={`Select comment by ${row.authorName || "a deleted account"}`}
                        />
                      </td>
                    )}
                    <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                    <td className="max_width">
                      {row.text}
                      {row.editedAt ? (
                        <span className="text-muted small"> (edited)</span>
                      ) : null}
                    </td>
                    <td>
                      {row.authorName || <span className="text-muted">Deleted account</span>}
                      {row.authorEmail ? (
                        <div className="text-muted small">{row.authorEmail}</div>
                      ) : null}
                    </td>
                    <td>{row.postTitle || <span className="text-muted">Untitled</span>}</td>
                    <td>{formatWhen(row.commentAt)}</td>
                    <td>
                      <Can feature="comments" action="delete">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          disabled={deletingId === row._id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(row);
                          }}
                          aria-label="Delete comment"
                        >
                          <Trash2 size={16} />
                        </button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={pages}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
              onPageChange={(next) => setUrl({ page: String(next) })}
              showItemsPerPage={false}
              showTotalItems
            />
          )}
        </>
      )}

      {viewing && (
        <CommentDetail
          comment={viewing}
          onClose={() => setViewing(null)}
          onDelete={() => {
            const row = viewing;
            setViewing(null);
            handleDelete(row);
          }}
        />
      )}
    </div>
  );
};

export default Comments;
